import { repository } from 'redux-scaffolding-ts';
import { DataStore, Query, QueryResult } from 'src/core/stores/dataStore';
import { container } from 'src/inversify.config';
import HttpService from '../core/services/http.service';
import { FormStore } from '../core/stores/formStore';
import { Validator } from "lakmus";
import { CommandResult } from '../core/stores/types';
import autobind from 'autobind-decorator';
import { title } from 'process';


export interface TaskTemplateSummary {
    id: string;
    title: string;
    reference: string;
    sticky: boolean;
    cirEligibility: boolean;
    ciiEligibility: boolean;
    projectTemplates: ProjectTemplateSummary[];
}

export interface NewTaskTemplateItem {
    title: string;
    reference: string;
    companyId: string;
    sticky: boolean;
    cirEligibility: boolean;
    ciiEligibility: boolean;
    projectTemplates: string[];
}

export interface NewTaskTemplateError {
    title?: string;
    message: string;
}

export interface TaskTemplateItem extends NewTaskTemplateItem {
    id: string;
}

export interface ProjectTemplateSummary {
    id: string;
    title: string;
}

export interface NewProjectTemplateItem {
    companyId:string,
    title: string,
}

export interface ProjectTemplateItem extends NewProjectTemplateItem {
    id: string;
}



@repository("@@TASKTEMPLATES", "taskTemplates.sumary")
export class TaskTemplatesStore extends DataStore<TaskTemplateSummary> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/task-templates`;

    constructor() {
        super('TASKTEMPLATE', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/task-templates/${companyId}`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<TaskTemplateSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<TaskTemplateSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async getTaskTemplates(query: Query, data?: any): Promise<QueryResult<TaskTemplateSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get<QueryResult<TaskTemplateSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data);
        return result.data;
    }
}

export class NewTaskTemplateValidator extends Validator<NewTaskTemplateItem> {
    constructor() {
        super();
    }
}

@repository("@@TASKTEMPLATES", "taskTemplates.new")
export class NewTaskTemplateStore extends FormStore<NewTaskTemplateItem> {
    baseUrl: string = ``;
    errorMessages: NewTaskTemplateError [] = [];

    protected validate(item: NewTaskTemplateItem) {
        return (new NewTaskTemplateValidator()).validate(item);
    }

    constructor() {
        super('NEW_TASKTEMPLATE', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/task-templates/${companyId}`;
    }

    public clearErrorMessages() {
        this.errorMessages = [];
    }

    @autobind
    public async post(): Promise<CommandResult<NewTaskTemplateItem>> {
        const validationResult = this.validate(this.state.item as NewTaskTemplateItem);
        if (!validationResult || !validationResult.isValid) {
            this.dispatch(this.ENTITY_VALIDATED, validationResult);
            return Promise.resolve({
                isSuccess: false,
            } as CommandResult<NewTaskTemplateItem>);
        } else {
            var httpService = this.container.get<HttpService>(HttpService);
            var item = this.state.item;

            return await this.dispatchAsync(this.ENTITY_SAVE, new Promise<CommandResult<NewTaskTemplateItem>>((resolve, reject) => {
                httpService.post(`${this.baseUrl}`, item)
                    .then(async result => {
                        let resultData = (result.data as any)
                        if (resultData.isSuccess) {
                            resolve(result.data as any);
                        }
                    })
                    .catch(error => {
                        if (item) {
                            this.errorMessages.push({
                                title: item.title,
                                message: error.message
                            })
                        }
                        reject(error)
                    })
            }));
        }
    }
}

@repository("@@PROJECTTEMPLATES", "projectTemplates.summary")
export class ProjectTemplatesStore extends DataStore<ProjectTemplateSummary> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/task-templates`;

    constructor() {
        super('PROJECTTEMPLATE', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/project-templates/${companyId}`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectTemplateSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectTemplateSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }
}


export class NewProjectTemplateValidator extends Validator<NewProjectTemplateItem> {
    constructor() {
        super();
    }
}

export class ProjectTemplateValidator extends Validator<NewProjectTemplateItem> {
    constructor() {
        super();
    }
}

@repository("@@PROJECTTEMPLATES", "projectTemplates.new")
export class NewProjectTemplateStore extends FormStore<NewProjectTemplateItem> {
    baseUrl: string = ``;

    protected validate(item: NewProjectTemplateItem) {
        return (new NewProjectTemplateValidator()).validate(item);
    }

    constructor() {
        super('NEW_PROJECTTEMPLATE', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/project-templates/${companyId}`;
    }
}


const PROJECT_TEMPLATE_UPDATE_ITEM = "PROJECT_UPDATE_ITEM";
@repository("@@PROJECTTEMPLATES", "projectTemplate.detail")
export class ProjectTemplateStore extends FormStore<ProjectTemplateItem> {
    baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/project-templates`;

    protected validate(item: ProjectTemplateItem) {
        return new ProjectTemplateValidator().validate(item);
    }

    constructor() {
        super('PROJECT_TEMPLATE', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
        this.addReducer(PROJECT_TEMPLATE_UPDATE_ITEM, (): any => {
            return super.onPatch()
        }, 'AsyncAction');
    }

    public async getById(id: string, params?: any, companyId?: string): Promise<ProjectTemplateItem> {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LOAD, httpService.get<ProjectTemplateItem>(`${this.baseUrl}/${companyId}/${id}`, params));
        return result.data;
    }

    public async update(item: ProjectTemplateItem) {
        var result = await super.patch(PROJECT_TEMPLATE_UPDATE_ITEM, `${item.id}`, item) as any;
        return result.data as CommandResult<ProjectTemplateItem>;
    }
}